import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import { COUNTRIES } from './CountryConstants/constants';
import { useUiTheme } from 'framework/uiTheme';
import { getVarsPalette } from 'framework/ui/themes';

type CountryBadgeProps = {
  country: string;
  sx?: SxProps;
};

const UNKNOWN_FLAG = 'un';

const COUNTRY_MAP_BY_NAME = COUNTRIES.reduce<Record<string, string>>((acc, { name, code }) => {
  acc[name.toLowerCase()] = code;
  return acc;
}, {});

const CountryBadge: React.FC<CountryBadgeProps> = ({ country, sx }) => {
  const [flagSrc, setFlagSrc] = useState<string | null>(null);
  const { InputLoader } = useUiTheme();
  const flag = useMemo(() => {
    if (typeof country === 'undefined') {
      return UNKNOWN_FLAG;
    }
    const normalized = country.trim();
    return COUNTRY_MAP_BY_NAME[normalized.toLowerCase()] ?? country ?? UNKNOWN_FLAG;
  }, [country]);

  useEffect(() => {
    let flagImg = null;
    const loadFlag = async () => {
      try {
        flagImg = await import(`assets/images/flags/${flag.toLowerCase()}.webp`);
        setFlagSrc(flagImg.default);
      } catch (error) {
        flagImg = await import(`assets/images/flags/un.webp`);
        setFlagSrc(flagImg.default);
      }
    };

    loadFlag();
  }, [country, flag]);

  if (!flagSrc) {
    return <InputLoader className="files-list__files-loader" />;
  }

  return (
    <Box
      sx={{
        padding: 0,
        width: 24,
        height: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 20,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={flagSrc}
        width="24px"
        height={`${flag.toLowerCase() === UNKNOWN_FLAG.toLowerCase() ? '24px' : '20px'}`}
        alt={country}
        sx={{
          filter:
            flag.toLowerCase() === UNKNOWN_FLAG.toLowerCase()
              ? 'none'
              : theme => `drop-shadow(0px 0px 1px ${getVarsPalette(theme).common.shadowContrast})`,
        }}
      />
    </Box>
  );
};
export default CountryBadge;
